import { convert } from "html-to-text";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";
import { useEffect } from "react";
import EventGalleryChildItem from "../../components/EventGalleryChildItem";
import { MainLayout } from "../../components/layouts/MainLayout";
import DesignTinkersService from "../../services/DesignTinkersService";
import { ISR_REVALIDATE_SECONDS } from "../../lib/ssr-cache";
import { EventGallery } from "../../types/EventGallery";

const EventGalleryDetailPage = ({ title, childs, cover_image_url,  }: EventGallery) => {
  const router = useRouter();

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  let newsUrl
  if (typeof window !== 'undefined') {
    newsUrl = window.location.href
  }

  // html to text - summary 150 word
  let summary = convert(childs[0].description).substring(0, 150)+'...'
  
  // remove new line
  summary = summary.replace(/(\r\n|\n|\r)/gm, "");
  

  return (
    <MainLayout pageTitle={title}>
      <Head>
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={summary} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={newsUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={summary} />
        <meta property="og:image" content={cover_image_url.lg || ''} />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={newsUrl} />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={summary} />
        <meta property="twitter:image" content={cover_image_url.lg || ''} />
      </Head>
      {childs.map((child) => (
        <EventGalleryChildItem key={child.id} {...child} />
      ))}
    </MainLayout>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { slug } = context.params as IParams;

  try {
    const resp = await DesignTinkersService.getEventGalleryDetail(slug);
    return {
      props: resp.data.data,
      revalidate: ISR_REVALIDATE_SECONDS,
    };
  } catch (error: any) {
    // Only a real API 404 means the slug is gone; a timeout/5xx/network drop
    // must not be cached as a 404 (see pages/event/[slug].tsx).
    if (error?.response?.status === 404) {
      return { notFound: true, revalidate: ISR_REVALIDATE_SECONDS };
    }
    throw error;
  }
};

interface IParams extends ParsedUrlQuery {
  slug: string;
}

export const getStaticPaths: GetStaticPaths = async () => {
  // No paths are prerendered at build time. Every detail page renders on its
  // first request (fallback: "blocking") and is then kept fresh by on-demand
  // revalidation from the AMS backend plus the ISR_REVALIDATE_SECONDS interval
  // in getStaticProps. Prerendering the full catalogue (~560 pages) made
  // `next build` take ~6 minutes on the 1-CPU / 2 GB droplet, long enough for
  // the deploy's SSH session to drop mid-build and leave `.next` half-written
  // (site down with a missing prerender-manifest, 2026-09-21).
  return { paths: [], fallback: "blocking" };
};

export default EventGalleryDetailPage;
